import { useState, useRef } from 'react';
import { X, Printer, Download, LayoutTemplate, Maximize, Smartphone, Share2 } from 'lucide-react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { formatPrintDateTime, formatEventDateTime } from '../utils/dateUtils';
import { QrDownloadFormat, QR_FORMAT_OPTIONS, qrDownloadFileName, serializeSvg, canvasToPngBlob, triggerFileDownload } from '../utils/qrDownload';
import { QrFormatPicker } from './QrFormatPicker';
import { QrShareFlow } from './QrShareFlow';

interface QrPrintModalProps {
  eventName: string;
  eventDate: string;       // "YYYY-MM-DD"
  startTime: string;       // "HH:MM" (24hr)
  endTime?: string;        // "HH:MM" (24hr), optional
  eventUrl: string;        // The short URL slug (e.g., "my-event-abc")
  location?: string;       // Event location/venue name
  address?: string;        // Event street address
  contactInfo?: { name?: string; phone?: string; email?: string };
  onClose: () => void;
}

type PrintLayout = 'full-page' | 'business-cards' | 'full-page-nfc' | 'business-cards-nfc';

export default function QrPrintModal({ eventName, eventDate, startTime, endTime, eventUrl, location, address, contactInfo, onClose }: QrPrintModalProps) {
  const [layout, setLayout] = useState<PrintLayout>('full-page');
  const printRef = useRef<HTMLDivElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const qrSvgRef = useRef<SVGSVGElement>(null);
  const [showFormatPicker, setShowFormatPicker] = useState(false);
  const [showShareFlow, setShowShareFlow] = useState(false);

  const fullPageUrl = `${window.location.origin}/e/${eventUrl}`;
  const qrUrlSmall = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullPageUrl)}`;
  const qrUrlLarge = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(fullPageUrl)}`;
  const nfcLogoUrl = `${window.location.origin}/nfc-logo.webp`;
  const formattedDateTime = formatPrintDateTime(eventDate, startTime, endTime);
  const isNfc = layout === 'full-page-nfc' || layout === 'business-cards-nfc';
  const isFullPage = layout === 'full-page' || layout === 'full-page-nfc';

  const layoutFlags = (printLayout: PrintLayout) => ({
    nfc: printLayout === 'full-page-nfc' || printLayout === 'business-cards-nfc',
    fullPage: printLayout === 'full-page' || printLayout === 'full-page-nfc',
  });

  const getStyles = (printLayout: PrintLayout): string => {
    const { nfc: isNfc, fullPage: isFullPage } = layoutFlags(printLayout);
    if (isFullPage) {
      return `
        @page { margin: 0.5in; size: letter portrait; }
        body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
        .print-container { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; text-align: center; }
        .event-name { font-size: 28pt; font-weight: bold; margin-bottom: 16px; color: #111; }
        .qr-img { width: ${isNfc ? '4in' : '5in'}; height: ${isNfc ? '4in' : '5in'}; margin: 24px auto; }
        .event-url { font-size: 14pt; color: #555; margin-top: 16px; word-break: break-all; }
        .event-details { font-size: 12pt; color: #777; margin-top: 4px; margin-bottom: 8px; }
        .event-location { font-size: 11pt; color: #666; margin-top: 4px; }
        .event-contact { font-size: 10pt; color: #888; margin-top: 4px; }
        .cta-text { font-size: 14pt; color: #333; font-weight: 500; margin-top: 20px; font-style: italic; }
        .nfc-section { display: flex; align-items: center; justify-content: center; gap: 0; margin-top: 32px; padding-top: 24px; border-top: 1px solid #ddd; }
        .nfc-arrow { font-size: 28pt; color: #1d4ed8; line-height: 1; }
        .nfc-arrow-left { margin-right: 8px; }
        .nfc-arrow-right { margin-left: 8px; }
        .nfc-center { display: flex; flex-direction: column; align-items: center; }
        .nfc-logo { width: 1.5in; height: auto; }
        .nfc-cta { font-size: 14pt; color: #1d4ed8; font-weight: 600; margin-top: 6px; }
      `;
    }
    return `
      @page { margin: 0.25in; size: letter portrait; }
      body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
      .cards-grid { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(5, 1fr); width: 7.5in; height: 10in; gap: 0; }
      .card { width: 3.5in; height: 2in; border: 1px dashed #ccc; display: flex; align-items: center; padding: 0.15in; box-sizing: border-box; overflow: hidden; }
      .card-qr { width: 1.5in; height: 1.5in; flex-shrink: 0; margin-right: 0.15in; }
      .card-info { flex: 1; min-width: 0; }
      .card-name { font-size: 10pt; font-weight: bold; color: #111; margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .card-date { font-size: 8pt; color: #555; margin-bottom: 2px; }
      .card-url { font-size: 7pt; color: #888; word-break: break-all; }
      .card-branding { font-size: 6pt; color: #333; margin-top: 4px; font-style: italic; }
      .card-nfc { display: flex; align-items: center; gap: 3px; margin-top: 2px; }
      .card-nfc-arrow { font-size: 7pt; color: #1d4ed8; line-height: 1; }
      .card-nfc-logo { width: 0.4in; height: auto; }
      .card-nfc-cta { font-size: 7pt; color: #1d4ed8; font-weight: 500; }
    `;
  };

  const getHtml = (printLayout: PrintLayout): string => {
    const { nfc: isNfc, fullPage: isFullPage } = layoutFlags(printLayout);
    if (isFullPage) {
      const contactParts: string[] = [];
      if (contactInfo?.name) contactParts.push(escapeHtml(contactInfo.name));
      if (contactInfo?.phone) contactParts.push(escapeHtml(contactInfo.phone));
      if (contactInfo?.email) contactParts.push(escapeHtml(contactInfo.email));

      return `
        <div class="print-container">
          <div class="event-name">${escapeHtml(eventName)}</div>
          <div class="event-details">${escapeHtml(formattedDateTime)}</div>
          ${location ? `<div class="event-location">${escapeHtml(location)}</div>` : ''}
          ${address ? `<div class="event-contact">${escapeHtml(address)}</div>` : ''}
          ${contactParts.length > 0 ? `<div class="event-contact">${contactParts.join(' &bull; ')}</div>` : ''}
          <img class="qr-img" src="${qrUrlLarge}" alt="QR Code" />
          <div class="cta-text">${isNfc
            ? 'Scan the QR code or tap your phone to the NFC tag to request songs and influence the dance playlist!'
            : 'Scan the QR code to request songs and influence the dance playlist!'}</div>
          <div class="event-url">${escapeHtml(fullPageUrl)}</div>
          ${isNfc ? `
            <div class="nfc-section">
              <span class="nfc-arrow nfc-arrow-left">\u25B6\u25B6</span>
              <div class="nfc-center">
                <img class="nfc-logo" src="${nfcLogoUrl}" alt="NFC" />
                <span class="nfc-cta">Tap your phone here to join!</span>
              </div>
              <span class="nfc-arrow nfc-arrow-right">\u25C0\u25C0</span>
            </div>
          ` : ''}
        </div>
      `;
    }
    return `
      <div class="cards-grid">
        ${Array.from({ length: 10 }).map(() => `
          <div class="card">
            <img class="card-qr" src="${qrUrlSmall}" alt="QR" />
            <div class="card-info">
              <div class="card-name">${escapeHtml(eventName)}</div>
              <div class="card-date">${escapeHtml(formattedDateTime)}</div>
              ${location ? `<div class="card-date">${escapeHtml(location)}${address ? ` \u2022 ${escapeHtml(address)}` : ''}</div>` : ''}
              <div class="card-url">${escapeHtml(fullPageUrl)}</div>
              ${isNfc ? `
                <div class="card-branding">Scan or tap to request songs!</div>
                <div class="card-nfc">
                  <span class="card-nfc-arrow">\u25B6</span>
                  <img class="card-nfc-logo" src="${nfcLogoUrl}" alt="NFC" />
                  <span class="card-nfc-cta">Tap to join!</span>
                </div>
              ` : `
                <div class="card-branding">Scan to request songs!</div>
              `}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  };

  const handlePrint = (layoutOverride?: PrintLayout) => {
    const printLayout = layoutOverride ?? layout;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${escapeHtml(eventName)}</title>
          <style>${getStyles(printLayout)}</style>
        </head>
        <body>${getHtml(printLayout)}</body>
      </html>
    `);
    printWindow.document.close();

    // Wait for ALL images to load then print
    const images = printWindow.document.querySelectorAll('img');
    if (images.length > 0) {
      let loaded = 0;
      let printed = false;
      const tryPrint = () => {
        loaded++;
        if (loaded >= images.length && !printed) {
          printed = true;
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 300);
        }
      };
      images.forEach(img => {
        if (img.complete) { tryPrint(); }
        else {
          img.onload = tryPrint;
          img.onerror = tryPrint;
        }
      });
      // Fallback timeout
      setTimeout(() => {
        if (!printed) {
          printed = true;
          printWindow.print();
          printWindow.close();
        }
      }, 5000);
    } else {
      printWindow.print();
      printWindow.close();
    }
  };

  const createPngBlob = (): Promise<Blob> => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return Promise.reject(new Error('QR canvas is not ready'));
    return canvasToPngBlob(canvas);
  };

  const createSvgBlob = (): Blob => {
    const svg = qrSvgRef.current;
    if (!svg) throw new Error('QR SVG is not ready');
    return new Blob([serializeSvg(svg)], { type: 'image/svg+xml' });
  };

  const handleDownload = async (format: QrDownloadFormat) => {
    setShowFormatPicker(false);
    if (format === 'pdf') {
      setLayout('full-page');
      handlePrint('full-page');
      return;
    }
    try {
      const blob = format === 'png' ? await createPngBlob() : createSvgBlob();
      triggerFileDownload(blob, qrDownloadFileName(eventUrl, format));
    } catch (err) {
      console.error('[QrPrintModal] Download failed:', err);
      toast.error('Could not create the QR file. Try again or use Print / Save PDF.');
    }
  };

  const layoutOptions: { key: PrintLayout; icon: typeof Maximize; label: string; desc: string }[] = [
    { key: 'full-page', icon: Maximize, label: 'Full Page', desc: 'Single large QR code' },
    { key: 'full-page-nfc', icon: Maximize, label: 'Full Page + NFC', desc: 'QR code with NFC tap' },
    { key: 'business-cards', icon: LayoutTemplate, label: 'Business Cards', desc: '10 cards per page' },
    { key: 'business-cards-nfc', icon: LayoutTemplate, label: 'Cards + NFC', desc: '10 cards with NFC tap' },
  ];

  const previewHelpText: Record<PrintLayout, string> = {
    'full-page': 'Prints as a full 8.5\u00d711 page',
    'business-cards': 'Prints 10 cards (3.5\u00d72\u201d) with cut lines',
    'full-page-nfc': 'Prints full page with NFC tap indicator',
    'business-cards-nfc': 'Prints 10 cards with NFC tap indicator',
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Printer className="w-5 h-5 text-primary" /> Print QR Code
          </h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Choose a layout for printing your event QR code.
        </p>

        {/* Layout Selection — 2x2 grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {layoutOptions.map(({ key, icon: Icon, label, desc }) => (
            <button
              key={key}
              onClick={() => setLayout(key)}
              className={`p-3 rounded-lg border-2 text-center transition-colors ${
                layout === key
                  ? 'border-primary bg-primary/5 dark:bg-primary/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Icon className={`w-6 h-6 ${layout === key ? 'text-primary' : 'text-gray-400'}`} />
                {(key === 'full-page-nfc' || key === 'business-cards-nfc') && (
                  <Smartphone className={`w-4 h-4 ${layout === key ? 'text-blue-500' : 'text-gray-400'}`} />
                )}
              </div>
              <span className={`text-sm font-medium ${layout === key ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                {label}
              </span>
              <p className="text-xs text-gray-500 mt-1">{desc}</p>
            </button>
          ))}
        </div>

        {/* Preview */}
        <div ref={printRef} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
          {isFullPage ? (
            <div className="text-center">
              <p className="font-bold text-gray-900 dark:text-white text-sm mb-1">{eventName}</p>
              <p className="text-xs text-gray-500 mb-1">{formattedDateTime}</p>
              {location && <p className="text-xs text-gray-400">{location}</p>}
              {address && <p className="text-xs text-gray-400">{address}</p>}
              {(contactInfo?.name || contactInfo?.phone || contactInfo?.email) && (
                <p className="text-xs text-gray-400">
                  {[contactInfo?.name, contactInfo?.phone, contactInfo?.email].filter(Boolean).join(' \u2022 ')}
                </p>
              )}
              <img src={qrUrlSmall} alt="QR Preview" className="w-24 h-24 mx-auto my-2" />
              <p className="text-xs text-gray-600 dark:text-gray-300 italic mt-1 mb-1">
                {isNfc
                  ? 'Scan the QR code or tap your phone to the NFC tag to request songs and influence the dance playlist!'
                  : 'Scan the QR code to request songs and influence the dance playlist!'}
              </p>
              <p className="text-xs text-gray-500 break-all">{fullPageUrl}</p>
              {isNfc && (
                <div className="flex items-center justify-center gap-1 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-blue-600 dark:text-blue-400 text-xs">{'\u25B6\u25B6'}</span>
                  <div className="flex flex-col items-center">
                    <img src={nfcLogoUrl} alt="NFC" className="w-12 h-auto" />
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Tap your phone here to join!</span>
                  </div>
                  <span className="text-blue-600 dark:text-blue-400 text-xs">{'\u25C0\u25C0'}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 border border-dashed border-gray-300 dark:border-gray-600 rounded p-2">
              <img src={qrUrlSmall} alt="QR Preview" className="w-14 h-14 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{eventName}</p>
                <p className="text-xs text-gray-500">{formattedDateTime}</p>
                <p className="text-xs text-gray-400 truncate">{fullPageUrl}</p>
                {isNfc ? (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-blue-600 dark:text-blue-400" style={{ fontSize: '6px' }}>{'\u25B6'}</span>
                    <img src={nfcLogoUrl} alt="NFC" className="w-6 h-auto" />
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Tap to join!</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-300">Scan to request songs!</p>
                )}
              </div>
            </div>
          )}
          <p className="text-center text-xs text-gray-400 mt-2 italic">
            {previewHelpText[layout]}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={onClose} className="btn btn-secondary flex-1">Cancel</button>
          <button onClick={() => handlePrint()} className="btn btn-primary flex-1 flex items-center justify-center gap-2">
            <Printer className="w-4 h-4" />
            Print / Save PDF
          </button>
          <button onClick={() => setShowFormatPicker(true)} className="btn btn-secondary flex-1 flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
        <button
          onClick={() => setShowShareFlow(true)}
          className="btn btn-secondary w-full mt-3 flex items-center justify-center gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share link or QR code
        </button>

        {showFormatPicker && (
          <QrFormatPicker
            title="Download QR code as"
            options={QR_FORMAT_OPTIONS}
            onPick={handleDownload}
            onCancel={() => setShowFormatPicker(false)}
          />
        )}

        {showShareFlow && (
          <QrShareFlow
            eventName={eventName}
            eventDateTime={formatEventDateTime(eventDate, startTime)}
            eventLink={fullPageUrl}
            eventUrl={eventUrl}
            createPngBlob={createPngBlob}
            createSvgBlob={createSvgBlob}
            onClose={() => setShowShareFlow(false)}
          />
        )}

        <div className="hidden" aria-hidden="true">
          <QRCodeCanvas value={fullPageUrl} size={1024} marginSize={4} level="M" ref={qrCanvasRef} />
          <QRCodeSVG value={fullPageUrl} size={1024} marginSize={4} level="M" ref={qrSvgRef} />
        </div>
      </div>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
